/**
 * A CMS preview token is a credential that travels in the URL
 * (`/<lang>/preview/<token>`, app/[lang]/(public)/preview/[token]). The API's
 * rule for it: don't log it, don't put it in an analytics event. Three things
 * here would otherwise do exactly that with the page URL — the first-party
 * `page_view` (components/analytics/PageView.tsx → `logEvent`), the PostHog
 * `$pageview` (`$current_url`) and Sentry's request URL / transaction name — so
 * every one of them passes the URL through this first. The segment after
 * `/preview/` becomes the literal `[token]`; everything else is untouched, so
 * the signal ("a preview was viewed") survives and the credential does not.
 * Pure, no imports: the client bundle, the Sentry init and vitest all share it.
 */
const PREVIEW_TOKEN = /(\/preview\/)[^/?#]+/g;

export function scrubPreviewToken(url: string): string {
  return url.replace(PREVIEW_TOKEN, "$1[token]");
}

/**
 * Sentry `beforeSend` / `beforeSendTransaction`: scrub the request URL, the
 * transaction name and navigation breadcrumbs (`from` / `to` / `url`). Generic
 * so the SDK's own event types pass through unchanged.
 */
export function scrubSentryEvent<
  E extends {
    request?: { url?: string };
    transaction?: string;
    breadcrumbs?: { data?: Record<string, unknown> }[];
  },
>(event: E): E {
  if (event.request?.url) event.request.url = scrubPreviewToken(event.request.url);
  if (event.transaction) event.transaction = scrubPreviewToken(event.transaction);
  for (const crumb of event.breadcrumbs ?? []) {
    const data = crumb.data;
    if (!data) continue;
    for (const key of ["from", "to", "url"]) {
      const v = data[key];
      if (typeof v === "string") data[key] = scrubPreviewToken(v);
    }
  }
  return event;
}
